import {Contract, ContractPreview, FnolRequest, FnolRequestStatus, ParsedInvoiceData, Treatment, TreatmentVerificationStatus} from "../models";
import {MissingTranslationHandler} from "@ngx-translate/core/lib/missing-translation-handler";
import { ApiHttpService } from "../services/api-http.service";
import { encode } from "base64-arraybuffer";
import jsPDF from "jspdf";

/**
 * Round a number to the nearest 0.05 (5 centimes)
 * @param v - Number to round
 * @returns Rounded number
 */
export const roundToFiveCoins = (v: number): number => {
  const roundingFactor = 0.05;
  let value = v ? v : 0;
  value = isNaN(value) ? 0 : value;
  const roundedValue = Math.round(value / roundingFactor) * roundingFactor;
  return roundedValue;
}

/**
 * Round a number to the nearest 0.05 (5 centimes) and return as string
 * @param v - Number to round
 * @returns Rounded number as string with 2 decimal places
 */
export const roundToFiveCoinsString = (v: number): string => {
  return roundToFiveCoins(v).toFixed(2);
}

export const convertContractsToContractPreview = (contracts: Contract[]): ContractPreview[] => {
  return contracts.reduce((acc: ContractPreview[], item: Contract) => {
    const contractNumberFirstPart = item.full_contract_number.split('-')[0];
    if (acc.length === 0 || acc[acc.length - 1].contractNumberFirstPart !== contractNumberFirstPart) {
      acc.push({
        full_contract_number: item.full_contract_number,
        contractNumberFirstPart: contractNumberFirstPart,
        pet_name: item.pet_name,
        firstname: item.firstname,
        lastname: item.lastname,
        email: item.email,
        phoneNumber: item.phoneNumber,
        birthdate: item.birthdate,
        contracts: [item]
      });
    } else {
      acc[acc.length - 1].contracts.push(item);
    }
    return acc;
  }, [])
}

export const getRecipientNameCheckStatus = (fnolRequest: FnolRequest, contract: Contract): TreatmentVerificationStatus => {
  let successfulComparison = 0;

  const fnolFullName = `${fnolRequest.fnolFirstName} ${fnolRequest.fnolLastName}`;
  const contractFullName = `${contract.firstname} ${contract.lastname}`;

  if (compareNames(fnolRequest.parsedInvoiceData.invoiceMetadata.recipientName, fnolFullName)) {
    successfulComparison++;
  }

  if (compareNames(fnolRequest.parsedInvoiceData.invoiceMetadata.recipientName, contractFullName)) {
    successfulComparison++;
  }

  if (compareNames(fnolFullName, contractFullName)) {
    successfulComparison++;
  }

  if (successfulComparison === 3) {
    return TreatmentVerificationStatus.OK;
  }

  if (successfulComparison > 0) {
    return TreatmentVerificationStatus.WARNING;
  }

  return TreatmentVerificationStatus.ERROR;
}

export const compareNames = (name1: string, name2: string) => {
  return name1.trim().toLowerCase() === name2.trim().toLowerCase();
}

export const getPetNameCheckStatus = (fnolRequest: FnolRequest, treatment: Treatment, contract: Contract): TreatmentVerificationStatus => {
  var cleanPetNameFromTreatment = cleanPetName(treatment.petName)

  if (!compareNames(cleanPetNameFromTreatment, contract.pet_name)) {
    return TreatmentVerificationStatus.ERROR;
  }
  
  if (!compareNames(cleanPetNameFromTreatment, fnolRequest.fnolPetName)) {
    return TreatmentVerificationStatus.WARNING;
  }
  // given that we did not return on the previous two conditions, we can infer that all petName instances are equal
  return TreatmentVerificationStatus.OK;

}

function cleanPetName(name: string): string {
  // Remove common prefixes/suffixes and anything in parentheses
  return name
    .replace(/\b(Hund|Katze|Dog|Cat|Chien|Chat|Cane|Gatto)\b/gi, '') // Adjust this list based on your needs
    .replace(/\(.*?\)/g, '') // Remove anything within parentheses
    .trim(); // Remove leading and trailing whitespace
}

export const checkIfTreatmentDateBeforeContractStartDate = (contractStartDate: string, treatmentDate: string) => {
  return new Date(treatmentDate).getTime() < new Date(contractStartDate).getTime();
}

export const checkIfTreatmentDateAfterContractEndDate = (contractEndDate: string, treatmentDate: string) => {
  return new Date(treatmentDate).getTime() > new Date(contractEndDate).getTime();
}

export const checkIfTreatmentDateIsBetweenContractDates = (contractStartDate: string, contractEndDate: string, treatmentDate: string) => {
  return new Date(treatmentDate).getTime() >= new Date(contractStartDate).getTime() && new Date(treatmentDate).getTime() <= new Date(contractEndDate).getTime();
}

export const getDateCheckStatus = (treatment: Treatment, contract: Contract): TreatmentVerificationStatus => {
  if (checkIfTreatmentDateIsBetweenContractDates(contract.start_date, contract.end_date, treatment.date)) {
    return TreatmentVerificationStatus.OK;
  }

  return TreatmentVerificationStatus.ERROR;
}

/**
 * Calculate pet's age at a specific treatment date
 * @param petBirthDate - Pet's birth date as string
 * @param treatmentDate - Treatment date as string
 * @returns Age in years at treatment date
 */
export const calculatePetAgeAtTreatmentDate = (petBirthDate: string, treatmentDate: string): number => {
  const petBirthDateObj = new Date(petBirthDate);
  const treatmentDateObj = new Date(treatmentDate);
  
  let age = treatmentDateObj.getFullYear() - petBirthDateObj.getFullYear();
  
  if (petBirthDateObj.getMonth() > treatmentDateObj.getMonth()) {
    age--;
  } 

  else if (petBirthDateObj.getMonth() === treatmentDateObj.getMonth()) {
    if (petBirthDateObj.getDate() > treatmentDateObj.getDate()) {
      age--;
    }
  }
  
  return age;
}

/**
 * Calculate adjusted amount based on pet age and deductible percentages
 * @param originalAmount - Original amount before deductible
 * @param petAge - Pet's age at treatment date
 * @param deductibleUntil9Years - Deductible percentage for pets under 9 years
 * @param deductibleAfter9Years - Deductible percentage for pets over 9 years
 * @returns Adjusted amount after applying deductible
 */
export const calculateAdjustedAmount = (
  originalAmount: number,
  petAge: number,
  deductibleUntil9Years: number,
  deductibleAfter9Years: number
): number => {
  let deductiblePercentage: number;
  
  if (petAge >= 9) {
    deductiblePercentage = deductibleAfter9Years;
  } else {
    deductiblePercentage = deductibleUntil9Years;
  }
  
  const adjustedAmount = originalAmount * (1 - deductiblePercentage / 100);
  
  // Apply roundToFiveCoins logic
  return roundToFiveCoins(adjustedAmount);
}

export const getAddressCheckStatus = (fnolRequest: FnolRequest, contract: Contract): TreatmentVerificationStatus => {
  const minEntrancesOK = 3;
  const minEntrancesWarning = 1;
  const contractAddressEntrances = [...contract.street.split(' '), contract.houseNumber, ...contract.city.split(' '), contract.zip].filter(e => e !== '').map(e => e.toLowerCase());
  const fnolAddressEntrances = fnolRequest.parsedInvoiceData.invoiceMetadata.recipientAddress.split(' ').filter(e => e !== '').map(e => e.toLowerCase());

  let successfulComparison = 0;

  contractAddressEntrances.forEach(e => {
    if (fnolAddressEntrances.includes(e)) {
      successfulComparison++;
    }
  });

  if (successfulComparison >= minEntrancesOK) {
    return TreatmentVerificationStatus.OK;
  }

  if (successfulComparison >= minEntrancesWarning) {
    return TreatmentVerificationStatus.WARNING;
  }

  return TreatmentVerificationStatus.ERROR;
}

export const contractIdFromContractNumber = (contractNumber?: string): number => {
  if (!contractNumber) {
    return 0
  }
  let contractIdStr = contractNumber.startsWith("PET") ? contractNumber.substring("PET".length) : contractNumber
  let contractId = parseInt(contractIdStr)
  if (isNaN(contractId)) {
    return 0
  }
  return contractId
}

export class HandleMissingTranslation implements MissingTranslationHandler {
  handle(params: any): string {
    console.log('Missing translation for:', params.key);
    return params.key;
  }
}

export function createEmptyParsedInvoiceData(): ParsedInvoiceData {

  const emptyParsedInvoiceData: ParsedInvoiceData = {
    invoiceMetadata: {
      issuerName: "",
      issuerAddress: "",
      recipientName: "",
      recipientAddress: "",
      invoiceNumber: "",
      invoiceDate: "",
      language: ""
    },
    recognizedVet: undefined,  // Optional property
    treatments: [],  // Empty array for treatments
    sumWithoutVat: 0,
    sumWithVat: 0,
    calculatedSum: 0,
    calculationInformation: {
      vatContainedInPositions: false,
      totalInclVat: 0,
      totalExclVat: 0,
      vatAmountToDistribute: 0,
      vatPercentageToDistribute: 0,
      applicableDiscountAmount: 0,

    }
  };

  return emptyParsedInvoiceData
}

export function checkSimpleClaim(fnolRequest: FnolRequest) {
  if (fnolRequest.initialReserveData) {
    if (fnolRequest.initialReserveData.type === 'praevention') {
      return true
    }

    if (fnolRequest.initialReserveData.type === 'unfall') {
      const assignedContract = findAssignedContract(fnolRequest, fnolRequest.initialReserveData.fullContractNumber)
      if (assignedContract && assignedContract.start_date && fnolRequest.initialReserveData.claimDate) {
        const daysDifference = getDaysDifference(assignedContract.start_date, fnolRequest.initialReserveData.claimDate)
        if (daysDifference && daysDifference >= 180) {
          return true
        }
      }
    }

    if (fnolRequest.initialReserveData.amount === 1) {
      return true
    }

  }
  return false
}

export function findAssignedContract(fnolRequest: FnolRequest, fullContractNumber: string): Contract | null {
  return fnolRequest.parsedInvoiceData?.treatments?.find(t => t.contract?.full_contract_number === fullContractNumber)?.contract || null
}

export function getDaysDifference(dateStr1: string, dateStr2: string): number | null {
  if (!dateStr1 || !dateStr2) return null;
  const date1 = new Date(dateStr1);
  const date2 = new Date(dateStr2);
  if (isNaN(date1.getTime()) || isNaN(date2.getTime())) return null;
  const diffInMs = Math.abs(date2.getTime() - date1.getTime());
  return Math.floor(diffInMs / (1000 * 60 * 60 * 24));
}

export function statusString(status: FnolRequestStatus): string {
  switch (status) {
    case FnolRequestStatus.TODO:
      return 'fnol-request-list.statuses.TODO';
    case FnolRequestStatus.FNOL_DATA_CONFIRMED:
      return 'fnol-request-list.statuses.FNOL_DATA_CONFIRMED';
    case FnolRequestStatus.INVOICE_PARSED_CONFIRMED:
      return 'fnol-request-list.statuses.INVOICE_PARSED_CONFIRMED';
    case FnolRequestStatus.VAT_AND_DISCOUNT_SET:
      return 'fnol-request-list.statuses.VAT_AND_DISCOUNT_SET';
    case FnolRequestStatus.CONTRACTS_ASSIGNED:
      return 'fnol-request-list.statuses.CONTRACTS_ASSIGNED';
    case FnolRequestStatus.TREATMENT_VALIDATED:
      return 'fnol-request-list.statuses.TREATMENT_VALIDATED';
    case FnolRequestStatus.INVOICE_ITEMS_TO_CONTRACT_ASSIGNED:
      return 'fnol-request-list.statuses.INVOICE_ITEMS_TO_CONTRACT_ASSIGNED';
    case FnolRequestStatus.CLASSIFICATIONS_ASSIGNED:
      return 'fnol-request-list.statuses.CLASSIFICATIONS_ASSIGNED';
    case FnolRequestStatus.USED_UP_FRANCHISE_FILLED:
      return 'fnol-request-list.statuses.USED_UP_FRANCHISE_FILLED';
    case FnolRequestStatus.PAYMENT_CALCULATED:
      return 'fnol-request-list.statuses.PAYMENT_CALCULATED';
    case FnolRequestStatus.DONE:
      return 'fnol-request-list.statuses.DONE';
    case FnolRequestStatus.NO_PROCESSING:
      return 'fnol-request-list.statuses.NO_PROCESSING'
    default:
      return 'fnol-request-list.statuses.UNKNOWN';
  }
}

export function getStatusCategory(status: FnolRequestStatus): string {
  switch (status) {
    // NEW
    case FnolRequestStatus.TODO:
    case FnolRequestStatus.FNOL_DATA_CONFIRMED:
      return 'fnol-request-list.status-new';
    
    // IN PROGRESS
    case FnolRequestStatus.INVOICE_PARSED_CONFIRMED:
    case FnolRequestStatus.VAT_AND_DISCOUNT_SET:
    case FnolRequestStatus.CONTRACTS_ASSIGNED:
    case FnolRequestStatus.INVOICE_ITEMS_TO_CONTRACT_ASSIGNED:
    case FnolRequestStatus.TREATMENT_VALIDATED:
    case FnolRequestStatus.CLASSIFICATIONS_ASSIGNED:
    case FnolRequestStatus.CLASSIFICATIONS_VALIDATED:
    case FnolRequestStatus.USED_UP_FRANCHISE_FILLED:
    case FnolRequestStatus.PAYMENT_CALCULATED:
      return 'fnol-request-list.status-in-progress';
    
    // DONE
    case FnolRequestStatus.NO_PROCESSING:
    case FnolRequestStatus.DONE:
      return 'fnol-request-list.status-completed';
    
    default:
      return 'fnol-request-list.status-unknown';
  }
}

export function getRandomString(length: number): string {
  const letters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz';
  let result = '';
  for (let i = 0; i < length; i++) {
    result += letters.charAt(Math.floor(Math.random() * letters.length));
  }
  return result;
}

export async function openOrDownload(fnolRequestId: string, s3Id: string, apiHttpService: ApiHttpService) {
  if (fnolRequestId) {
    try {
      const location = (await apiHttpService.getFileDownloadUrl(fnolRequestId, s3Id)).Location;
      window.open(location, '_blank');
    } catch (e) {
      console.error(e);
    }
  }
}

export function generateFnolSummaryPDF(fnolRequest: FnolRequest, save:boolean = true): string | null {
  const doc = new jsPDF();
  const pageHeight= doc.internal.pageSize.height;

  // Define mappings from JSON keys to label texts
  const mappings: { [key: string]: string } = {
    notificationDate: 'Meldedatum',
    fnolFirstName: 'Vorname',
    fnolLastName: 'Nachname',
    fnolEmail: 'Email',
    fnolPhoneNumber: 'Telefonnummer',
    fnolPreferredChannel: 'Bevorzugter Kommunikationskanal',
    fnolLang: 'Sprache',
    fnolIban: 'IBAN',
    fnolPetName: 'Haustiername',
    fnolConditionPreviouslyTreated: 'Wurde das Leiden bereits zuvor behandelt',
    fnolDateConditionFirstTreated: 'Datum der ersten Behandlung',
    fnolDateConditionLastTreated: 'Datum der letzten Behandlung',
    fnolDateConditionNoticed: 'Datum wann das Leiden bemerkt wurde',
    fnolDateOfTreatment: 'Datum der Behandlung',
    fnolThirdPartyFault: 'War eine Drittpartei verantwortlich',
    fnolAdditionalInformation: 'Weitere Informationen',
  };

  let y = 10; // Vertical position on the PDF
  const lineHeight = 10;

  if (!fnolRequest) {
    console.warn('cannot create pdf of fnol request data as fnolRequest is null or undefined')
    return null
  }

  // Go through each key-value pair in the data and map to the PDF
  for (const [key, label] of Object.entries(mappings)) {
    const value = fnolRequest[key as keyof FnolRequest];
    let displayValue = value == null ? '-' : value.toString();

    // Convert booleans to "Ja" or "Nein"
    if (typeof value === 'boolean') {
      displayValue = value ? 'Ja' : 'Nein';
    }
    // Convert -1 or "--" for specific fields as needed
    if (value === -1 || value === "--") {
      displayValue = '--';
    }

    // Wrap the value text if it's too long
    const wrappedText = doc.splitTextToSize(`${label}: ${displayValue}`, 180);

    y = writeToPDF(doc, wrappedText, 10, y, lineHeight, pageHeight);
    
  }

  // Handling the Diagnosen array
  y = writeToPDF(doc, 'Diagnosen:', 10, y, lineHeight, pageHeight);
  if (fnolRequest.fnolDiagnoses && fnolRequest.fnolDiagnoses.length > 0) {
    fnolRequest.fnolDiagnoses.forEach(diagnosis => {
      const diagnosisText = `- ${diagnosis.id} (Category: ${diagnosis.category})`;
      const wrappedDiagnosis = doc.splitTextToSize(diagnosisText, 180);
      y = writeToPDF(doc, wrappedDiagnosis, 20, y, lineHeight, pageHeight);
    });
  } else {
    y = writeToPDF(doc, '-', 20, y, lineHeight, pageHeight);
  }

  // Save the PDF
  let docName = nameFnolSummaryPdf(fnolRequest)
  if (save) {
    doc.save(docName.replace('.','_'));
  }
  const dataUriResult = doc.output('datauristring');
  let base64Doc = getBase64FromDataUri(dataUriResult);

  // console.log('base64Doc', this.base64Doc);
  return base64Doc;
}

export function nameFnolSummaryPdf(fnolRequest: FnolRequest) {
  return 'claim-details_' + fnolRequest.fnolFirstName + '_' + fnolRequest.fnolPetName + '_' + fnolRequest.notificationDate;
}

export function writeToPDF(doc: jsPDF, wrappedText: string | string[], x: number, y: number, lineHeight: number, pageHeight: number){
    
  if (typeof wrappedText === 'string'){
      wrappedText = [wrappedText]
  }
  
  for (const item of wrappedText){

    if (y >= pageHeight){
      doc.addPage();
      y = 10;
    }

    doc.text(item, x, y);
    y += lineHeight
  }
  
  return y;
}

export function getBase64FromDataUri(dataUri: string): string | null {
  const parts = dataUri.split(',');
  if (parts.length < 2) {
    return null;
  }
  return parts[1];
}

export function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      // Remove the data URL prefix (data:application/vnd.openxmlformats-officedocument.wordprocessingml.document;base64,)
      const base64 = result.split(',')[1];
      resolve(base64);
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}